/**
 * Pure maths for the print layout (Wave 5.4): page sizes, scale bar, scale denominator,
 * north arrow, graticule. All screen units are CSS px at 96 dpi; MapLibre's world is
 * 512 px per tile at zoom 0.
 */

export const TILE_SIZE = 512;
export const EARTH_CIRCUMFERENCE_M = 40075016.686;
export const CSS_DPI = 96;
export const MM_PER_INCH = 25.4;
export const M_PER_CSS_PX_ON_PAPER = (MM_PER_INCH / CSS_DPI) / 1000; // 0.000264583 m
export const FEET_PER_M = 3.280839895;
export const FEET_PER_MILE = 5280;

// ── Page sizes ──────────────────────────────────────────────────────────────

/** Portrait dimensions in mm (jsPDF formats: 'a4' | 'a3' | 'letter'). */
export const PAGE_SIZES = {
  a4: { label: 'A4', wMm: 210, hMm: 297 },
  a3: { label: 'A3', wMm: 297, hMm: 420 },
  letter: { label: 'Letter', wMm: 215.9, hMm: 279.4 },
};

export const mmToPx = (mm) => Math.round((mm * CSS_DPI) / MM_PER_INCH);

export function pageDimensions(size = 'a4', orientation = 'landscape') {
  const s = PAGE_SIZES[size] || PAGE_SIZES.a4;
  const land = orientation === 'landscape';
  const widthMm = land ? s.hMm : s.wMm;
  const heightMm = land ? s.wMm : s.hMm;
  return { widthMm, heightMm, widthPx: mmToPx(widthMm), heightPx: mmToPx(heightMm) };
}

export const DPI_CHOICES = [150, 300];

/** html2canvas scale for a target dpi (the page node is laid out at 96 dpi). */
export const dpiToScale = (dpi) => dpi / CSS_DPI;

// ── Page composition ────────────────────────────────────────────────────────

export const LEGEND_PLACEMENTS = [
  { id: 'bottom-right', label: 'Bottom right corner' },
  { id: 'bottom-left', label: 'Bottom left corner' },
  { id: 'top-right', label: 'Top right corner' },
  { id: 'top-left', label: 'Top left corner' },
  { id: 'side', label: 'Side column' },
];

export const PAGE_MARGIN = 24;
export const HEADER_H = 64;
export const FOOTER_H = 44;
export const SIDE_COLUMN_W = 210;

/** Rectangles (px, relative to the page) for the map frame and the optional side column. */
export function composePage(pageW, pageH, legendPlacement = 'bottom-right', showLegend = true) {
  const side = showLegend && legendPlacement === 'side';
  const x = PAGE_MARGIN;
  const y = PAGE_MARGIN + HEADER_H;
  const bodyW = pageW - PAGE_MARGIN * 2;
  const h = pageH - y - FOOTER_H - PAGE_MARGIN;
  const w = side ? bodyW - SIDE_COLUMN_W - 12 : bodyW;
  return {
    map: { x, y, w, h },
    side: side ? { x: x + w + 12, y, w: SIDE_COLUMN_W, h } : null,
  };
}

/**
 * Place the captured map image (cssW x cssH of the live canvas) in the frame. At 1:1 when it
 * is at least as large as the frame (centre crop: scale stays true), otherwise magnified to
 * cover it. `k` is the magnification (>= 1) the scale maths must divide by.
 */
export function fitMapImage({ mapW, mapH, frameW, frameH }) {
  if (!(mapW > 0) || !(mapH > 0)) return { width: frameW, height: frameH, left: 0, top: 0, k: 1 };
  const k = Math.max(1, frameW / mapW, frameH / mapH);
  const width = mapW * k;
  const height = mapH * k;
  return { width, height, left: (frameW - width) / 2, top: (frameH - height) / 2, k };
}

// ── Scale ───────────────────────────────────────────────────────────────────

/** Ground resolution of MapLibre at latitude/zoom, metres per CSS px. */
export function metersPerPixel(lat, zoom, tileSize = TILE_SIZE) {
  return (EARTH_CIRCUMFERENCE_M * Math.cos((lat * Math.PI) / 180)) / (tileSize * 2 ** zoom);
}

/** Largest 1/2/5 x 10^n that is <= max (0 when max is not positive). */
export function niceNumber(max) {
  if (!(max > 0) || !Number.isFinite(max)) return 0;
  const exp = Math.floor(Math.log10(max));
  const base = 10 ** exp;
  const m = max / base;
  const nice = m >= 5 ? 5 : m >= 2 ? 2 : 1;
  return nice * base;
}

/**
 * A scale bar no longer than maxPx.
 * @returns {{ value:number, unit:string, label:string, lengthPx:number, meters:number }}
 */
export function niceScaleBar(mpp, maxPx, units = 'metric') {
  if (!(mpp > 0) || !(maxPx > 0)) return { value: 0, unit: units === 'metric' ? 'm' : 'ft', label: '', lengthPx: 0, meters: 0 };
  const maxM = mpp * maxPx;
  let value; let unit; let meters;
  if (units === 'imperial') {
    const maxFt = maxM * FEET_PER_M;
    if (maxFt >= FEET_PER_MILE) { value = niceNumber(maxFt / FEET_PER_MILE); unit = 'mi'; meters = (value * FEET_PER_MILE) / FEET_PER_M; }
    else { value = niceNumber(maxFt); unit = 'ft'; meters = value / FEET_PER_M; }
  } else if (maxM >= 1000) {
    value = niceNumber(maxM / 1000); unit = 'km'; meters = value * 1000;
  } else {
    value = niceNumber(maxM); unit = 'm'; meters = value;
  }
  return { value, unit, label: `${formatNumber(value)} ${unit}`, lengthPx: meters / mpp, meters };
}

export function formatNumber(v) {
  if (v >= 1000) return v.toLocaleString('en-US');
  return String(+v.toPrecision(6));
}

/** Map scale denominator for a capture shown at magnification k (paper at 96 css dpi). */
export function scaleDenominator(mpp, k = 1) {
  return mpp / k / M_PER_CSS_PX_ON_PAPER;
}

/** Zoom that gives 1:denominator at magnification k. */
export function zoomForScale(denominator, lat, k = 1, tileSize = TILE_SIZE) {
  const mpp = denominator * M_PER_CSS_PX_ON_PAPER * k;
  return Math.log2((EARTH_CIRCUMFERENCE_M * Math.cos((lat * Math.PI) / 180)) / (tileSize * mpp));
}

/** "1:5,000"; rounded to two significant figures once past 1:100. */
export function formatScale(denominator) {
  if (!(denominator > 0)) return '';
  const d = denominator >= 100 ? Number(denominator.toPrecision(2)) : Math.round(denominator);
  return `1:${d.toLocaleString('en-US')}`;
}

// ── North arrow ─────────────────────────────────────────────────────────────

/** CSS rotation (deg, clockwise) of an up-pointing north arrow for a map bearing. */
export function northArrowRotation(bearing = 0) {
  let r = -(bearing || 0);
  r = ((r % 360) + 360) % 360;
  if (r > 180) r -= 360;
  return r === 0 ? 0 : r; // avoid -0
}

// ── Projection helpers (Web Mercator, as MapLibre) ──────────────────────────

const clampLat = (lat) => Math.max(-85.05112878, Math.min(85.05112878, lat));

export function lngLatToWorldPx(lng, lat, zoom) {
  const s = TILE_SIZE * 2 ** zoom;
  const sin = Math.sin((clampLat(lat) * Math.PI) / 180);
  return [((lng + 180) / 360) * s, (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * s];
}

export function worldPxToLngLat(x, y, zoom) {
  const s = TILE_SIZE * 2 ** zoom;
  const lng = (x / s) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / s;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return [lng, lat];
}

/** view = { center:[lng,lat], zoom, bearing, k }; frame = { w, h }. */
export function projectToFrame([lng, lat], view, frame) {
  const [cx, cy] = lngLatToWorldPx(view.center[0], view.center[1], view.zoom);
  const [px, py] = lngLatToWorldPx(lng, lat, view.zoom);
  const k = view.k || 1;
  const dx = (px - cx) * k;
  const dy = (py - cy) * k;
  const b = ((view.bearing || 0) * Math.PI) / 180;
  return [frame.w / 2 + dx * Math.cos(b) + dy * Math.sin(b), frame.h / 2 - dx * Math.sin(b) + dy * Math.cos(b)];
}

export function unprojectFromFrame([x, y], view, frame) {
  const k = view.k || 1;
  const sx = x - frame.w / 2;
  const sy = y - frame.h / 2;
  const b = ((view.bearing || 0) * Math.PI) / 180;
  const dx = (sx * Math.cos(b) - sy * Math.sin(b)) / k;
  const dy = (sx * Math.sin(b) + sy * Math.cos(b)) / k;
  const [cx, cy] = lngLatToWorldPx(view.center[0], view.center[1], view.zoom);
  return worldPxToLngLat(cx + dx, cy + dy, view.zoom);
}

// ── Graticule ───────────────────────────────────────────────────────────────

export const GRATICULE_INTERVALS = [
  0.0001, 0.0002, 0.0005, 0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 30,
];

/** Smallest standard interval that gives at most `maxLines` lines across the span. */
export function graticuleInterval(spanDeg, maxLines = 6) {
  const span = Math.abs(spanDeg);
  for (const i of GRATICULE_INTERVALS) {
    if (span / i <= maxLines) return i;
  }
  return GRATICULE_INTERVALS[GRATICULE_INTERVALS.length - 1];
}

export function formatDegrees(value, interval, axis) {
  const decimals = Math.max(0, Math.ceil(-Math.log10(interval) - 1e-9));
  const hemi = axis === 'lng' ? (value < 0 ? 'W' : 'E') : (value < 0 ? 'S' : 'N');
  return `${Math.abs(value).toFixed(decimals)}°${hemi}`;
}

/** Liang-Barsky clip of a segment to the rectangle [0,w]x[0,h]; null when outside. */
export function clipSegment([x1, y1], [x2, y2], w, h) {
  let t0 = 0; let t1 = 1;
  const dx = x2 - x1; const dy = y2 - y1;
  const p = [-dx, dx, -dy, dy];
  const q = [x1, w - x1, y1, h - y1];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) { if (q[i] < 0) return null; continue; }
    const r = q[i] / p[i];
    if (p[i] < 0) { if (r > t1) return null; if (r > t0) t0 = r; }
    else { if (r < t0) return null; if (r < t1) t1 = r; }
  }
  return [[x1 + t0 * dx, y1 + t0 * dy], [x1 + t1 * dx, y1 + t1 * dy]];
}

/**
 * Lon/lat lines for the frame: [{ axis, value, label, p1, p2, labelAt }] in frame px.
 * Meridians and parallels are straight in Web Mercator, also when the map is rotated.
 */
export function graticuleLines(view, frame, { maxLines = 6 } = {}) {
  const corners = [[0, 0], [frame.w, 0], [frame.w, frame.h], [0, frame.h]].map((c) => unprojectFromFrame(c, view, frame));
  const lngs = corners.map((c) => c[0]);
  const lats = corners.map((c) => c[1]);
  const lngMin = Math.min(...lngs); const lngMax = Math.max(...lngs);
  const latMin = Math.min(...lats); const latMax = Math.max(...lats);
  const interval = graticuleInterval(Math.max(lngMax - lngMin, latMax - latMin), maxLines);
  const out = [];
  const first = (min) => Math.ceil(min / interval - 1e-9) * interval;
  const clean = (v) => +v.toFixed(8);
  for (let lng = first(lngMin), n = 0; lng <= lngMax + 1e-12 && n < 40; lng += interval, n++) {
    const v = clean(lng);
    const seg = clipSegment(projectToFrame([v, latMin - 1], view, frame), projectToFrame([v, latMax + 1], view, frame), frame.w, frame.h);
    if (!seg) continue;
    const labelAt = seg[0][1] > seg[1][1] ? seg[0] : seg[1]; // lower end on screen
    out.push({ axis: 'lng', value: v, label: formatDegrees(v, interval, 'lng'), p1: seg[0], p2: seg[1], labelAt });
  }
  for (let lat = first(latMin), n = 0; lat <= latMax + 1e-12 && n < 40; lat += interval, n++) {
    const v = clean(lat);
    const seg = clipSegment(projectToFrame([lngMin - 1, v], view, frame), projectToFrame([lngMax + 1, v], view, frame), frame.w, frame.h);
    if (!seg) continue;
    const labelAt = seg[0][0] < seg[1][0] ? seg[0] : seg[1]; // left end on screen
    out.push({ axis: 'lat', value: v, label: formatDegrees(v, interval, 'lat'), p1: seg[0], p2: seg[1], labelAt });
  }
  return { interval, lines: out };
}

export function crsText(crsLabel) {
  return `Datum: WGS 84 · Map projection: Web Mercator (EPSG:3857)${crsLabel ? ` · Coordinates shown: ${crsLabel}` : ''}`;
}

/** Keep the north arrow and scale bar out of the corner the legend occupies. */
export function placeFurniture(legendPlacement = 'bottom-right', showLegend = true) {
  const l = showLegend ? legendPlacement : null;
  return {
    north: l === 'top-right' ? 'top-left' : 'top-right',
    scale: l === 'bottom-left' ? 'bottom-right' : 'bottom-left',
  };
}
